// ---------------------------------------------------------------------------
// Label schema, element factories and media presets.
//
// The document described here IS the persisted JSON (LabelTemplate.layout).
// Everything is stored in millimetres so a label survives a change of printer
// resolution; conversion to printer dots happens only in render/zplRenderer.
// ---------------------------------------------------------------------------

export const SCHEMA_VERSION = 1;

/** Dots per millimetre. The ZD230 is 203 dpi = 8 dots/mm. */
export const DPMM_203 = 8;
export const DPMM_300 = 12;

/** Die-cut stock in use. `columns` is how many stickers sit across the web. */
export const LABEL_PRESETS = [
  { label: "30 × 16 mm (3-across)", widthMm: 30, heightMm: 16, columns: 3, gapXMm: 3 },
  { label: "50 × 30 mm", widthMm: 50, heightMm: 30, columns: 1, gapXMm: 2 },
  { label: "58 × 40 mm", widthMm: 58, heightMm: 40, columns: 1, gapXMm: 2 },
  { label: "100 × 50 mm", widthMm: 100, heightMm: 50, columns: 1, gapXMm: 2 },
];

/**
 * Fields a label may reference as {placeholder}. Substituted per sticker at print
 * time from the production run being printed.
 */
export const LABEL_FIELDS = [
  { key: "barcode", label: "Product code" },
  { key: "productName", label: "Product name" },
  { key: "productionId", label: "Production ID" },
  { key: "billNumber", label: "Bill / batch number" },
  { key: "packedBy", label: "Packed by" },
  { key: "packedDate", label: "Packed date" },
  { key: "serial", label: "Serial" },
  { key: "index", label: "Position in run" },
  { key: "total", label: "Run size" },
];

/**
 * Separator between fields in a QR payload.
 *
 * Pipe-delimited rather than JSON on purpose: `{"b":"BILL-2291","s":"0417"}` spends
 * ~26 characters on quotes and braces that carry no information, which pushes the
 * symbol up a version or two. On a 16 mm sticker that is the difference between a
 * QR that fits and one that does not.
 */
export const FIELD_SEPARATOR = "|";

/**
 * Append `{key}` to a payload, inserting the separator only where one is needed —
 * not at the start, and not after a separator the user already typed.
 */
export function appendField(text, key, separator = FIELD_SEPARATOR) {
  const current = String(text == null ? "" : text);
  const token = `{${key}}`;
  if (!current) return token;
  if (!separator) return current + token;
  return current.trimEnd().endsWith(separator) ? current + token : current + separator + token;
}

/**
 * Fonts resident in the printer.
 *
 * Font 0 is the only scalable one and is proportional; A-H are bitmap, monospace,
 * and legal only at integer multiples of their base cell. There is no italic in any
 * resident ZPL font and no bold flag, so "bold" here is overprinting (see
 * zplRenderer) and italic is deliberately not offered rather than faked badly.
 */
export const ZPL_FONTS = [
  { key: "0", label: "ZEBRA 0 (scalable)", scalable: true, baseH: 0, baseW: 0 },
  { key: "A", label: "A — small", scalable: false, baseH: 9, baseW: 5 },
  { key: "B", label: "B — small caps", scalable: false, baseH: 11, baseW: 7 },
  { key: "D", label: "D — medium", scalable: false, baseH: 18, baseW: 10 },
  { key: "F", label: "F — bold block", scalable: false, baseH: 26, baseW: 13 },
  { key: "E", label: "E — OCR-B", scalable: false, baseH: 28, baseW: 15 },
  { key: "H", label: "H — OCR-A", scalable: false, baseH: 21, baseW: 13 },
  { key: "G", label: "G — large", scalable: false, baseH: 60, baseW: 40 },
];

export const fontByKey = (key) => ZPL_FONTS.find((f) => f.key === key) || ZPL_FONTS[0];

/**
 * Snap a requested height to something the chosen font can actually print.
 * Bitmap fonts only render at whole multiples of their cell, so asking for anything
 * else means the printer silently rounds and the preview stops matching the label.
 */
export function legalFontSize(fontKey, requestedH) {
  const f = fontByKey(fontKey);
  if (f.scalable) return { fontH: Math.max(6, Math.round(requestedH)), fontW: 0 };
  const mult = Math.max(1, Math.round(requestedH / f.baseH));
  return { fontH: f.baseH * mult, fontW: f.baseW * mult };
}

let seq = 0;
const nextId = (prefix) => `${prefix}-${Date.now().toString(36)}-${seq++}`;

export const makeQrElement = (overrides = {}) => ({
  id: nextId("qr"),
  type: "qr",
  xMm: 1,
  yMm: 1.5,
  magnification: 4, // dots per QR module
  ecLevel: "Q", // L | M | Q | H
  rotation: 0,
  text: `{barcode}${FIELD_SEPARATOR}{serial}`,
  ...overrides,
});

export const makeTextElement = (overrides = {}) => ({
  id: nextId("txt"),
  type: "text",
  xMm: 13,
  yMm: 6,
  widthMm: 16,
  // "printer" uses a resident ZPL font (^A, small payload, printer-scaled);
  // "system" rasterises a desktop font such as Arial to a ^GF bitmap.
  fontSource: "printer",
  fontKey: "0", // resident printer font; see ZPL_FONTS
  fontFamily: "Arial", // used when fontSource is "system"
  fontH: 20, // dots
  fontW: 0, // 0 = proportional, set only for bitmap fonts
  bold: false, // emulated by overprinting - ZPL has no bold flag
  italic: false, // only possible with a rasterised system font
  underline: false, // drawn with ^GB
  strikethrough: false, // drawn with ^GB
  align: "L", // L | C | R
  maxLines: 1,
  rotation: 0,
  // Plain placeholder text, not a field. What a label shows in print is a design
  // decision, so the designer types it; product details belong in the QR payload,
  // where a scan can return them.
  text: "Text",
  ...overrides,
});

/**
 * A blank label.
 *
 * Printer-setting commands (^PW, ^LL, ^MD) are deliberately off: the label carries
 * drawing commands only and the printer's own configuration decides width, length
 * and darkness. Turning one on makes a print job write a setting the printer keeps
 * afterwards, which then follows it to every other machine.
 */
export const defaultLabel = () => ({
  schemaVersion: SCHEMA_VERSION,
  dpmm: DPMM_203,
  widthMm: 30,
  heightMm: 16,
  paddingMm: 1.5,
  columns: 3,
  rows: 1,
  gapXMm: 3,
  gapYMm: 0,
  offsetXMm: 0,
  offsetYMm: 0,
  darkness: 10,
  setPrintWidth: false,
  setLabelLength: false,
  setDarkness: false,
  elements: [makeQrElement(), makeTextElement()],
});

/** Labels printed per page of the grid. */
export const labelsPerPage = (label) =>
  Math.max(1, (label.columns || 1) * (label.rows || 1));

/** Sticker pitch in mm — the number that has to match the physical roll. */
export const labelPitchMm = (label) => (label.widthMm || 0) + (label.gapXMm || 0);
